/**
 * CookieConsentBanner — iter239
 * ────────────────────────────────────────────────────────────
 * First-visit banner asking users to accept or decline non-essential
 * cookies. Choice is persisted in localStorage under `flyt_cookie_consent`
 * so the banner never returns after a decision. Fully accessible with
 * clear Accept / Decline buttons and a link out to the Privacy Policy.
 *
 * Design principles:
 *   • Anchored bottom (mobile-safe with pb-safe padding)
 *   • Solid dark background — never dims the underlying page or blocks
 *     interaction, so it feels like a "sticky note" the user can dismiss.
 *   • Respects users who already stored a preference — never shown again.
 *   • Emits a `cookieconsent-change` custom event so analytics loaders
 *     can gate themselves on the user's choice.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { isNativeApp } from '../lib/nativeBridge';

const STORAGE_KEY = 'flyt_cookie_consent';

/** Read the stored consent value ('accepted' | 'declined' | null). */
export const readCookieConsent = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'declined' ? v : null;
  } catch {
    return null;
  }
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // iter553 · Never render on native iOS (Capacitor shell). Apple
    // rejects apps that show web-style cookie/consent prompts because
    // native iOS apps don't run in a browser cookie context — we
    // don't drop tracking cookies inside the Capacitor WebView. This
    // banner is web-only from here on out.
    if (isNativeApp()) return;
    // Delay slightly so the banner doesn't fight the initial paint.
    const t = setTimeout(() => {
      if (!readCookieConsent()) setVisible(true);
    }, 700);
    return () => clearTimeout(t);
  }, []);

  const record = useCallback((value) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
      window.dispatchEvent(new CustomEvent('cookieconsent-change', { detail: { value } }));
    } catch {
      // localStorage disabled → banner just stays dismissed for this session.
    }
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[70] pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <div className="pointer-events-auto rounded-2xl bg-neutral-950 ring-1 ring-white/15 shadow-2xl p-4 sm:p-5 text-white">
        <div className="flex items-start gap-3">
          <span className="inline-flex w-8 h-8 rounded-full bg-blue-500/25 ring-1 ring-blue-400/40 items-center justify-center flex-shrink-0">
            <Cookie className="w-4 h-4 text-blue-200" />
          </span>
          <div className="flex-1 min-w-0">
            <h2
              id="cookie-consent-title"
              className="text-sm font-bold uppercase tracking-widest text-white mb-1"
            >
              Cookies on flythouse.com
            </h2>
            <p
              id="cookie-consent-desc"
              className="text-xs text-white/70 leading-snug"
            >
              We use essential cookies to keep you signed in and remember your
              preferences. With your consent we also use analytics cookies to
              understand how Flyt House™ is used so we can make it better. Read
              our{' '}
              <Link
                to="/privacy"
                className="underline text-white hover:text-blue-200"
                onClick={() => setVisible(false)}
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={() => record('declined')}
            aria-label="Dismiss and decline non-essential cookies"
            className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 inline-flex items-center justify-center flex-shrink-0"
            data-testid="cookie-consent-close"
          >
            <X className="w-3.5 h-3.5 text-white/60" />
          </button>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => record('declined')}
            className="flex-1 px-3 py-2 rounded-full bg-white/[0.06] hover:bg-white/[0.12] ring-1 ring-white/15 text-white text-xs font-bold uppercase tracking-widest transition"
            data-testid="cookie-consent-decline"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => record('accepted')}
            className="flex-1 px-3 py-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-xs font-black uppercase tracking-widest transition"
            data-testid="cookie-consent-accept"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
